import { useId, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ApiError,
  cancelReissue,
  listCertificateReissueRequests,
  listPayments,
  requestReissue,
  type CertificateDetail,
  type Payment,
  type ReissueRequest,
  type ReissueRequestCreate,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { Field, OtherProblems, inputClass, serverProblems, submitClass, useRevealProblems } from "@/components/form";
import { formatDate } from "@/components/format";
import { ErrorBanner, StatusBadge, ToolbarButton } from "@/components/ui";
import { formatAmount } from "@/features/payments/amount";
import { reissueIssueText } from "./issues";

const SERVER_FIELDS = ["reason", "mode", "payment_id", "fee_waiver_reason", "affidavit_reference", "affidavit_verified"] as const;
type ReissueField = (typeof SERVER_FIELDS)[number];

function reissueTagParts(cert: CertificateDetail) {
  if (!cert.reissue_of) return null;
  const n = /-R(\d+)$/.exec(cert.doc_control_id)?.[1];
  const root = cert.lineage.find((item) => item.id === cert.reissue_of);
  return n && root ? { n, root } : null;
}

/** `Re-issue R<n> of <root>` for a re-issue, null for an original. */
export function reissueTag(cert: CertificateDetail): string | null {
  const parts = reissueTagParts(cert);
  return parts ? `Re-issue R${parts.n} of ${parts.root.doc_control_id}` : null;
}

export function ReissueTag({ cert }: { cert: CertificateDetail }) {
  const parts = reissueTagParts(cert);
  if (!parts) return null;
  return (
    <span className="text-sm text-zinc-600">
      Re-issue R{parts.n} of{" "}
      <Link to="/certificates/$certificateId" params={{ certificateId: parts.root.id }} className="font-mono underline">
        {parts.root.doc_control_id}
      </Link>
    </span>
  );
}

export function ReissuePanel({ cert }: { cert: CertificateDetail }) {
  const { me, can } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const requests = useQuery({
    queryKey: ["reissue-requests", cert.id],
    queryFn: () => listCertificateReissueRequests(cert.id),
  });
  const items = [...(requests.data?.items ?? [])].sort((a, b) => b.requested_at.localeCompare(a.requested_at));
  const pending = items.find((r) => r.status === "pending");
  // Requests name their payment by id; the member's re-issue payments supply reference and amount.
  const paid = useQuery({
    queryKey: ["reissue-payment-labels", cert.member_id],
    queryFn: () => listPayments({ purpose: "reissue", memberId: cert.member_id, pageSize: 100 }),
    enabled: items.some((r) => r.payment_id),
  });
  const paymentText = (id: string) => {
    const p = paid.data?.items.find((x) => x.id === id);
    return p ? `${p.reference ?? "No reference"} · ${formatAmount(p.amount_minor, p.currency)}` : "Payment";
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["reissue-requests"] });
    await queryClient.invalidateQueries({ queryKey: ["certificate", cert.id] });
    await queryClient.invalidateQueries({ queryKey: ["reissue-payments", cert.member_id] });
  };
  const withdraw = useMutation({ mutationFn: (id: string) => cancelReissue(id), onSuccess: refresh });

  const canRequest = can("certificate.reissue") && cert.status === "generated" && !pending && requests.isSuccess;

  return (
    <aside className="h-fit space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Re-issue</h2>
      <ErrorBanner error={requests.error ?? withdraw.error} />
      {open && canRequest ? (
        <RequestForm
          cert={cert}
          onDone={async () => {
            setOpen(false);
            await refresh();
          }}
          onCancel={() => setOpen(false)}
        />
      ) : canRequest ? (
        <ToolbarButton tone="primary" onClick={() => setOpen(true)}>Request re-issue</ToolbarButton>
      ) : null}
      {items.length > 0 ? (
        <ul className="divide-y divide-zinc-100 text-sm">
          {items.map((r) => (
            <RequestItem
              key={r.id}
              request={r}
              mine={r.requested_by.id === me?.user.id}
              paymentText={paymentText}
              withdrawing={withdraw.isPending}
              onWithdraw={() => withdraw.mutate(r.id)}
            />
          ))}
        </ul>
      ) : null}
    </aside>
  );
}

function RequestItem({
  request: r,
  mine,
  paymentText,
  withdrawing,
  onWithdraw,
}: {
  request: ReissueRequest;
  mine: boolean;
  paymentText: (id: string) => string;
  withdrawing: boolean;
  onWithdraw: () => void;
}) {
  const { can } = useSession();
  return (
    <li className="space-y-1 py-3 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-zinc-500">
          Requested by {r.requested_by.name} · {formatDate(r.requested_at)}
        </span>
        <StatusBadge status={r.status} />
      </div>
      <p>{r.reason}</p>
      <p className="text-xs text-zinc-600">
        {r.mode === "revoke_original" ? "Revokes this certificate" : "Keeps this certificate valid"}
      </p>
      <p className="text-xs text-zinc-600">
        {r.payment_id ? paymentText(r.payment_id) : `Fee waived: ${r.fee_waiver_reason ?? "-"}`}
      </p>
      <p className="text-xs text-zinc-600">Affidavit {r.affidavit_reference}</p>
      {r.status === "rejected" ? (
        <p className="text-xs text-zinc-600">
          Rejected by {r.decided_by?.name ?? "-"}: {r.decision_comment}
        </p>
      ) : null}
      {r.status === "approved" ? <p className="text-xs text-zinc-600">Approved by {r.decided_by?.name ?? "-"}</p> : null}
      {r.status === "approved" && r.reissued_certificate_id ? (
        <Link
          to="/certificates/$certificateId"
          params={{ certificateId: r.reissued_certificate_id }}
          className="font-medium text-zinc-950 underline"
        >
          Open re-issued certificate
        </Link>
      ) : null}
      {r.status === "pending" && mine && can("certificate.reissue") ? (
        <ToolbarButton disabled={withdrawing} onClick={onWithdraw}>
          Withdraw request
        </ToolbarButton>
      ) : null}
      {r.status === "pending" && !mine && can("certificate.reissue.approve") ? (
        <Link to="/approvals/reissues" search={{ request: r.id }} className="block font-medium text-zinc-950 underline">
          Review in approvals
        </Link>
      ) : null}
    </li>
  );
}

type Values = {
  reason: string;
  mode: ReissueRequestCreate["mode"];
  fee: "payment" | "waive";
  payment_id: string;
  fee_waiver_reason: string;
  affidavit_reference: string;
  affidavit_verified: boolean;
};

function validate(v: Values): Partial<Record<ReissueField, string>> {
  const problems: Partial<Record<ReissueField, string>> = {};
  if (v.reason.trim() === "") problems.reason = "Give a reason.";
  if (v.affidavit_reference.trim() === "") problems.affidavit_reference = "Give the affidavit reference.";
  if (!v.affidavit_verified) problems.affidavit_verified = reissueIssueText("affidavit_required");
  if (v.fee === "payment" && v.payment_id === "") problems.payment_id = reissueIssueText("fee_choice");
  if (v.fee === "waive" && v.fee_waiver_reason.trim().length < 10) {
    problems.fee_waiver_reason = reissueIssueText("waiver_reason_short");
  }
  return problems;
}

function paymentLabel(p: Payment) {
  return `${formatAmount(p.amount_minor, p.currency)} · ${p.reference ?? "-"} · ${formatDate(p.confirmed_at ?? p.created_at)}`;
}

function RequestForm({
  cert,
  onDone,
  onCancel,
}: {
  cert: CertificateDetail;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<Values>({
    reason: "",
    mode: "keep_original",
    fee: "payment",
    payment_id: "",
    fee_waiver_reason: "",
    affidavit_reference: "",
    affidavit_verified: false,
  });
  const payments = useQuery({
    queryKey: ["reissue-payments", cert.member_id],
    queryFn: () =>
      listPayments({ purpose: "reissue", memberId: cert.member_id, status: "confirmed", unused: true, limit: 50 }),
  });
  const submit = useMutation({
    mutationFn: () => {
      const body: ReissueRequestCreate = {
        reason: values.reason.trim(),
        mode: values.mode,
        affidavit_reference: values.affidavit_reference.trim(),
        affidavit_verified: values.affidavit_verified,
      };
      if (values.fee === "payment") body.payment_id = values.payment_id;
      else body.fee_waiver_reason = values.fee_waiver_reason.trim();
      return requestReissue(cert.id, body);
    },
    onSuccess: onDone,
  });

  const formRef = useRef<HTMLFormElement>(null);
  const reveal = useRevealProblems<ReissueField>(formRef);
  const clientErrors = validate(values);
  const server = serverProblems(submit.error, SERVER_FIELDS);
  const problem = (field: ReissueField) => {
    const fromServer = server.byField[field];
    return reveal.shown(field, clientErrors[field]) ?? (fromServer ? reissueIssueText(fromServer) : undefined);
  };
  const set = <K extends keyof Values>(field: K, value: Values[K]) => {
    submit.reset(); // a server problem is stale once the form changes
    setValues((prev) => ({ ...prev, [field]: value }));
  };
  const validation = submit.error instanceof ApiError && submit.error.status === 422 && submit.error.details.length > 0;

  const affidavitId = useId();
  const choices = payments.data?.items ?? [];
  let paymentChoice: ReactNode;
  if (payments.isPending) {
    paymentChoice = <p className="text-xs text-zinc-500">Loading payments...</p>;
  } else if (payments.error) {
    paymentChoice = (
      <div className="space-y-1">
        <ErrorBanner error={payments.error} />
        {problem("payment_id") ? <p className="text-xs text-red-700">{problem("payment_id")}</p> : null}
      </div>
    );
  } else if (choices.length === 0) {
    paymentChoice = (
      <div>
        <p className="text-xs text-zinc-600">No confirmed, unused re-issue payment for this member. Record one in Payments.</p>
        {problem("payment_id") ? <p className="mt-1 text-xs text-red-700">{problem("payment_id")}</p> : null}
      </div>
    );
  } else {
    paymentChoice = (
      <Field label="Payment" problem={problem("payment_id")}>
        {(props) => (
          <select
            {...props}
            aria-label="Payment"
            value={values.payment_id}
            onChange={(e) => set("payment_id", e.target.value)}
            onBlur={() => reveal.touch("payment_id")}
            className={inputClass}
          >
            <option value="">Choose a payment</option>
            {choices.map((p) => (
              <option key={p.id} value={p.id}>
                {paymentLabel(p)}
              </option>
            ))}
          </select>
        )}
      </Field>
    );
  }

  return (
    <form
      ref={formRef}
      noValidate
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (Object.keys(clientErrors).length > 0) {
          reveal.submitted();
          return;
        }
        if (!submit.isPending) submit.mutate();
      }}
    >
      <Field label="Reason" problem={problem("reason")}>
        {(props) => (
          <textarea
            {...props}
            rows={3}
            maxLength={2000}
            value={values.reason}
            onChange={(e) => set("reason", e.target.value)}
            onBlur={() => reveal.touch("reason")}
            className={`${inputClass} resize-none`}
          />
        )}
      </Field>

      <fieldset className="space-y-1 text-sm">
        <legend className="font-medium text-zinc-700">What happens to this certificate?</legend>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="mode"
            checked={values.mode === "keep_original"}
            onChange={() => set("mode", "keep_original")}
          />
          Keep it valid
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="mode"
            checked={values.mode === "revoke_original"}
            onChange={() => set("mode", "revoke_original")}
          />
          Revoke it
        </label>
        <p className="pl-6 text-xs text-zinc-500">Starts a revocation that a second person must approve.</p>
        {problem("mode") ? <p className="text-xs text-red-700">{problem("mode")}</p> : null}
      </fieldset>

      <fieldset className="space-y-2 text-sm">
        <legend className="font-medium text-zinc-700">Fee</legend>
        <label className="flex items-center gap-2">
          <input type="radio" name="fee" checked={values.fee === "payment"} onChange={() => set("fee", "payment")} />
          Use a payment
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" name="fee" checked={values.fee === "waive"} onChange={() => set("fee", "waive")} />
          Waive the fee
        </label>
        {values.fee === "payment" ? (
          paymentChoice
        ) : (
          <Field label="Fee waiver reason" problem={problem("fee_waiver_reason")}>
            {(props) => (
              <textarea
                {...props}
                rows={2}
                maxLength={2000}
                value={values.fee_waiver_reason}
                onChange={(e) => set("fee_waiver_reason", e.target.value)}
                onBlur={() => reveal.touch("fee_waiver_reason")}
                className={`${inputClass} resize-none`}
              />
            )}
          </Field>
        )}
      </fieldset>

      <Field label="Affidavit reference" problem={problem("affidavit_reference")}>
        {(props) => (
          <input
            {...props}
            value={values.affidavit_reference}
            onChange={(e) => set("affidavit_reference", e.target.value)}
            onBlur={() => reveal.touch("affidavit_reference")}
            className={inputClass}
          />
        )}
      </Field>
      <div className="text-sm">
        <label className="flex items-center gap-2 text-zinc-700">
          <input
            type="checkbox"
            checked={values.affidavit_verified}
            onChange={(e) => set("affidavit_verified", e.target.checked)}
            onBlur={() => reveal.touch("affidavit_verified")}
            aria-invalid={problem("affidavit_verified") ? true : undefined}
            aria-describedby={problem("affidavit_verified") ? affidavitId : undefined}
          />
          I have seen and checked the affidavit
        </label>
        {problem("affidavit_verified") ? (
          <p id={affidavitId} className="mt-1 text-xs text-red-700">
            {problem("affidavit_verified")}
          </p>
        ) : null}
      </div>

      <p className="text-xs text-zinc-500">The re-issue prints the member's current details.</p>
      {validation ? null : <ErrorBanner error={submit.error} />}
      <OtherProblems lines={server.other} />
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={submit.isPending} className={submitClass}>
          Submit re-issue request
        </button>
        <ToolbarButton disabled={submit.isPending} onClick={onCancel}>
          Cancel
        </ToolbarButton>
      </div>
    </form>
  );
}
